import GlassCard from '../components/ui/GlassCard';
import IndicatorCard from '../components/dashboard/IndicatorCard';
import MonthSelector from '../components/dashboard/MonthSelector';
import PresupuestoSelector from '../components/dashboard/PresupuestoSelector';
import TransaccionListItem from '../components/dashboard/TransaccionListItem';
import { useDashboardData } from '../hooks/useDashboardData';
import { usePresupuesto } from '../context/PresupuestoContext';
import { formatCurrency, formatDate } from '../utils/formatters';

const ArrowDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
    <path d="M12 4v16M5 13l7 7 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
    <path d="M12 20V4M5 11l7-7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
    <path d="M3 7a2 2 0 0 1 2-2h12l4 4v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="17" cy="13" r="1.4" fill="currentColor" />
  </svg>
);

const PiggyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
    <path d="M5 12a7 7 0 0 1 13.5-2.5L21 11l-2 1v3a4 4 0 0 1-4 4h-1l-1 2h-3l-1-2H7a4 4 0 0 1-4-4v-1l2-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="16" cy="11" r="0.8" fill="currentColor" />
  </svg>
);

function rangeHint(feDesde, feHasta) {
  if (!feDesde || !feHasta) return '';
  return `${formatDate(feDesde)} → ${formatDate(feHasta)}`;
}

function EmptyTransactions() {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center text-sm text-white/45">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
        <WalletIcon />
      </span>
      <p>No hay transacciones registradas en este período.</p>
    </div>
  );
}

function Dashboard() {
  const {
    presupuestosActivos,
    isLoading: isLoadingPresupuestos,
    error: errorPresupuestos,
    cdPresupuestoSeleccionado,
    seleccionarPresupuesto,
  } = usePresupuesto();

  const {
    range,
    year,
    month,
    isCurrentMonth,
    retroceder,
    avanzar,
    irMesActual,
    gastos,
    ingresos,
    saldo,
    ahorrosResumen,
    detalle,
    isLoading,
    hasError,
    recargar,
  } = useDashboardData(cdPresupuestoSeleccionado);

  const totalIngresos = Number(ingresos?.totalIngresos ?? 0);
  const totalEgresos = Number(gastos?.totalEgresos ?? 0);
  const totalSaldo = Number(saldo?.saldoDisponible ?? 0);
  const ingresosCount = Number(ingresos?.cantidadOperaciones ?? 0);
  const egresosCount = Number(gastos?.cantidadOperaciones ?? 0);
  const saldoIndicador = saldo?.indicador ?? 'positivo';
  const rangeLabel = rangeHint(range.feDesde, range.feHasta);

  return (
    <div className="relative mx-auto w-full max-w-6xl px-5 pb-24 pt-10 sm:px-8 sm:pt-16">
      <div className="mb-6">
        <PresupuestoSelector
          presupuestos={presupuestosActivos}
          isLoading={isLoadingPresupuestos}
          error={errorPresupuestos}
          seleccionado={cdPresupuestoSeleccionado}
          onSelect={seleccionarPresupuesto}
        />
      </div>
      <header className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/55">
            <WalletIcon />
            Módulo 03
          </span>
          <h1 className="font-serif text-5xl leading-[0.95] tracking-tight sm:text-6xl">
            <span className="italic text-gradient">Dashboard</span>
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-white/55 sm:text-[15px]">
            Resumen financiero del mes en curso. Visualiza tus ingresos, egresos,
            ahorros y saldo disponible de un vistazo.
          </p>
          {hasError && (
            <p className="text-xs font-medium text-rose-300/85">
              No se pudieron cargar algunos indicadores del dashboard.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <MonthSelector
            year={year}
            month={month}
            onPrev={retroceder}
            onNext={avanzar}
            onToday={irMesActual}
            isCurrent={isCurrentMonth}
          />
          <button
            type="button"
            onClick={recargar}
            className="inline-flex h-10 items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-[11px] font-mono uppercase tracking-[0.18em] text-white/70 transition hover:bg-white/[0.08] hover:text-white"
          >
            Recargar
          </button>
        </div>
      </header>

      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <IndicatorCard
          title="Ingresos del mes"
          value={formatCurrency(totalIngresos)}
          subtitle={`${ingresosCount} operación${ingresosCount === 1 ? '' : 'es'} en el período`}
          hint={rangeLabel}
          icon={ArrowUpIcon}
          tone="positive"
          badge="Ingresos"
        />
        <IndicatorCard
          title="Egresos del mes"
          value={formatCurrency(totalEgresos)}
          subtitle={`${egresosCount} operación${egresosCount === 1 ? '' : 'es'} en el período`}
          hint={rangeLabel}
          icon={ArrowDownIcon}
          tone="alert"
          badge="Gastos"
        />
        <IndicatorCard
          title="Saldo disponible"
          value={formatCurrency(totalSaldo)}
          subtitle={
            saldoIndicador === 'alerta'
              ? 'Saldo en negativo · revisa tus egresos.'
              : 'Saldo positivo del período.'
          }
          hint={rangeLabel}
          icon={WalletIcon}
          tone={saldoIndicador === 'alerta' ? 'alert' : 'positive'}
          badge={saldoIndicador === 'alerta' ? 'Atención' : 'Disponible'}
        />
        <IndicatorCard
          title="Ahorros del mes"
          value={formatCurrency(ahorrosResumen.total)}
          subtitle={`${ahorrosResumen.operaciones} operación${ahorrosResumen.operaciones === 1 ? '' : 'es'} de ahorro`}
          hint={rangeLabel}
          icon={PiggyIcon}
          tone="neutral"
          badge="Ahorro"
        />
      </div>

      <section className="mt-10">
        <GlassCard className="p-0">
          <header className="flex items-center justify-between gap-4 border-b border-white/5 px-6 py-5">
            <div>
              <h2 className="font-serif text-2xl tracking-tight">
                <span className="italic text-gradient">Detalle del mes</span>
              </h2>
              <p className="mt-1 text-xs font-mono uppercase tracking-[0.18em] text-white/40">
                {detalle.length} movimiento{detalle.length === 1 ? '' : 's'} · {rangeLabel}
              </p>
            </div>
          </header>
          <div className="max-h-[480px] overflow-y-auto">
            {isLoading && detalle.length === 0 ? (
              <div className="flex items-center justify-center px-6 py-12">
                <div className="h-4 w-32 overflow-hidden rounded-full bg-white/5">
                  <div className="h-full w-full animate-shimmer bg-gradient-to-r from-transparent via-white/15 to-transparent bg-[length:200%_100%]" />
                </div>
              </div>
            ) : detalle.length === 0 ? (
              <EmptyTransactions />
            ) : (
              <ul className="divide-y divide-white/5">
                {detalle.map((transaccion) => (
                  <TransaccionListItem key={transaccion.cdTransaccion} transaccion={transaccion} />
                ))}
              </ul>
            )}
          </div>
        </GlassCard>
      </section>

      <footer className="mt-10 flex flex-col items-center justify-between gap-2 border-t border-white/5 pt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-white/30 sm:flex-row">
        <span>Control de Gastos · FE</span>
        <span>v0.1.0 — build {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}

export default Dashboard;